'use client';
import { logger } from '@lichtblick/logger';
import { useEffect, useState } from 'react';

import { ZipCodeField } from '@/components/form-fields/zip-code-field';
import { i18n } from '@/i18n';
import { CityInput } from './city-input';

const transformCity = ({ value }: { value: string; label: string }): { cityId: string; cityName: string } => {
  const [_plz, cityIdAndName] = value.split('|');
  const [cityId, cityName] = cityIdAndName.split('_');

  return { cityId, cityName };
};

export const fetchCities = async (zip: string): Promise<{ cityId: string; cityName: string }[] | undefined> => {
  let retryCount = 3;

  while (retryCount > 0) {
    let response: Response | undefined;

    try {
      response = await fetch(`/api/zip/${zip}/`);
    } catch (_e) {
      // handled via retryCounter
    }

    if (response?.status === 200) {
      try {
        return (await response.json()).map(transformCity);
      } catch (error) {
        logger.warn('[checkout-b2c] Failed parsing cities JSON', { error, zip });
      }
    }

    retryCount--;

    if (retryCount === 0) {
      logger.log('[checkout-b2c] Failed to fetch cities');
    }

    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  return undefined;
};

type ZipAndCityState = {
  zipCode: string | undefined;
  cityName: string | undefined;
  cityId: string | undefined;
  cities: { cityId: string; cityName: string }[];
  isFetching: boolean;
};

export const ZipAndCityInput: React.FC<{
  defaultZip: string | undefined;
  defaultCity: string | undefined;
  defaultCityId: string | undefined;
  cityLabel: string;
  zipCodeLabel: string;
}> = ({ defaultZip, defaultCity, defaultCityId, cityLabel, zipCodeLabel }) => {
  const [state, setState] = useState<ZipAndCityState>({
    zipCode: defaultZip,
    cityName: defaultCity,
    cityId: defaultCityId,
    cities: [],
    isFetching: false,
  });

  useEffect(() => {
    if (defaultZip && /^\d{5}$/.test(defaultZip)) {
      setState((prev) => ({ ...prev, isFetching: true }));
      fetchCities(defaultZip).then((cities) =>
        setState((prev) => ({ ...prev, cities: cities ?? [], isFetching: false })),
      );
    }
  }, [defaultZip]);

  return (
    <>
      <ZipCodeField
        defaultValue={state.zipCode}
        invalidZip={!state.cities.length && !state.isFetching && !!state.zipCode && /^\d{5}$/.test(state.zipCode)} // !! is okay for typescript, but Boolean() is not narrowing the type. wtf
        name="zip"
        label={zipCodeLabel}
        submitAction={(zip) => {
          setState((prev) => ({ ...prev, zipCode: zip, cityName: undefined, cityId: undefined, cities: [] }));
          if (zip && /^\d{5}$/.test(zip)) {
            setState((prev) => ({ ...prev, isFetching: true }));
            fetchCities(zip).then((cities) => {
              const autoselectCity = cities?.length === 1 ? cities[0] : undefined;
              setState((prev) => ({ ...prev, cities: cities ?? [], ...autoselectCity, isFetching: false }));
            });
          }
        }}
      />

      <CityInput
        cities={state.cities}
        defaultCityId={state.cityId}
        defaultCity={state.cityName}
        name="city"
        label={cityLabel}
        errorMessage={
          !state.cities.length && !!state.zipCode && /^\d{5}$/.test(state.zipCode) && !state.isFetching
            ? 'Bitte gib eine gültige PLZ ein.'
            : i18n.validation.missing
        }
        onChange={(event) => {
          const selectedCityId = event.target.value;
          const selectedCity = state.cities.find((city) => city.cityId === selectedCityId);

          setState((prev) => ({
            ...prev,
            cityId: selectedCity?.cityId,
            cityName: selectedCity?.cityName,
          }));
        }}
      />
    </>
  );
};
